/* eslint-disable @next/next/no-img-element */

import { Row } from "antd";
import { ReactNode, useEffect, useRef, useState } from "react";
import useBreakpoint from "antd/lib/grid/hooks/useBreakpoint";
import {
  DesktopCarousel,
  LeftArrowIcon,
  MobileCarousel,
  RightArrowIcon,
} from "./Carousel.style";

type CarouselProps = {
  children: ReactNode;
  itemsLength: number;
  activeDotColor: string;
  inactiveDotColor: string;
  mobileSlidesPerRow: number;
  desktopItemWidth: number;
  arrowSize: number;
  arrowIconSize: number;
  arrowMargin: number;
  containerWidthOffset: number;
  slickDotMarginTop: number;
  centerMode: boolean;
};

export const Carousel = ({
  children,
  itemsLength,
  activeDotColor,
  inactiveDotColor,
  mobileSlidesPerRow,
  desktopItemWidth,
  arrowSize,
  arrowIconSize,
  arrowMargin,
  containerWidthOffset,
  slickDotMarginTop,
  centerMode,
}: CarouselProps) => {
  const sliderRef = useRef(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [carouselWidth, setCarouselWidth] = useState<number>(0);

  const { lg } = useBreakpoint();

  const [maxSliderNumber, setMaxSliderNumber] = useState<number>(3);
  const [fillSliderNumber, setFillSliderNumber] = useState<number>(0);

  const onPrevSlide = () => {
    sliderRef?.current?.prev();
  };

  const onNextSlide = () => {
    sliderRef?.current?.next();
  };

  useEffect(() => {
    const calculateMaxSliderItems = () => {
      if (lg) {
        const containerWidth =
          containerRef?.current?.offsetWidth - containerWidthOffset;
        const sliderNumber = Math.floor(containerWidth / desktopItemWidth) || 1;
        const componentMaxSliderItemNumber =
          sliderNumber > 3 ? 3 : sliderNumber;
        const maxSliderRenderNumber = Math.min(
          componentMaxSliderItemNumber > 3 ? 3 : componentMaxSliderItemNumber,
          itemsLength || 0
        );

        const fillSliderRenderNumber =
          itemsLength <= maxSliderRenderNumber ||
          !itemsLength ||
          itemsLength % maxSliderRenderNumber === 0
            ? 0
            : maxSliderRenderNumber -
              ((itemsLength || 0) % maxSliderRenderNumber);
        setCarouselWidth(containerWidth);
        setMaxSliderNumber(maxSliderRenderNumber);
        setFillSliderNumber(centerMode ? 0 : fillSliderRenderNumber);
      } else {
        setMaxSliderNumber(1);
        setFillSliderNumber(0);
      }
    };

    calculateMaxSliderItems();
    if (window) {
      window.addEventListener("resize", calculateMaxSliderItems);
    }
    return () => {
      if (window) {
        window.removeEventListener("resize", calculateMaxSliderItems);
      }
    };
  }, [itemsLength, lg, desktopItemWidth, containerWidthOffset, centerMode]);

  return (
    <Row
      justify={lg && itemsLength > maxSliderNumber ? "space-between" : "center"}
      align="middle"
      ref={containerRef}
    >
      {itemsLength > maxSliderNumber && (
        <LeftArrowIcon
          onClick={onPrevSlide}
          size={arrowSize}
          margin={arrowMargin}
        >
          <img
            src="/static/arrow-left-icon.svg"
            alt="arrow-left-icon"
            width={arrowIconSize}
            height={arrowIconSize}
          />
        </LeftArrowIcon>
      )}
      {lg ? (
        <DesktopCarousel
          ref={sliderRef}
          slidesToShow={maxSliderNumber}
          activeDotColor={activeDotColor}
          inactiveDotColor={inactiveDotColor}
          slidesToScroll={maxSliderNumber}
          slickDotMarginTop={slickDotMarginTop}
          centerMode={centerMode}
          width={carouselWidth}
        >
          {children}
          {Array.from({
            length: fillSliderNumber,
          }).map(() => (
            <div />
          ))}
        </DesktopCarousel>
      ) : (
        <MobileCarousel
          ref={sliderRef}
          slidesPerRow={mobileSlidesPerRow}
          activeDotColor={activeDotColor}
          inactiveDotColor={inactiveDotColor}
          slickDotMarginTop={slickDotMarginTop}
        >
          {children}
        </MobileCarousel>
      )}
      {itemsLength > maxSliderNumber && (
        <RightArrowIcon
          onClick={onNextSlide}
          size={arrowSize}
          margin={arrowMargin}
        >
          <img
            src="/static/arrow-right-icon.svg"
            alt="arrow-right-icon"
            width={arrowIconSize}
            height={arrowIconSize}
          />
        </RightArrowIcon>
      )}
    </Row>
  );
};
